import EditTradeContext from "@shared/features/trades/EditTrade/EditTrade.context";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";
import { toFixed } from "@shared/utils/numbers";
import {
  getMinAndMaxPrices,
  validateOrderRelativePrice,
} from "@shared/features/tradingTerminal/common/utils";
import { LanguageNameSpaces, OrderStatus } from "@shared/types";
import {
  getAmountValidationMessage,
  getDefaultEditOrder,
  getOrdersDefaultSortOrder,
  getValidationMessage,
} from "@shared/features/trades/common/utils";
import { OrderTypes } from "@shared/features/trades/common/types";
import { sortByNumberProperty } from "@shared/utils/general";
import { useContext, useState } from "react";
import {
  EditOrderModalProps,
  EditTradeInputProps,
} from "@shared/features/trades/EditTrade/types";
import { isNumber } from "lodash";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const useAddOrEditOrderModal = ({
  order,
  orderType,
  onOk,
  closeModal,
}: EditOrderModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const {
    precisionConfig,
    currentPrice,
    ordersData,
    entryOrders,
    takeProfitOrders,
    amountData,
    convertAmountToQuantity,
    getMinAmountByPrice,
    isBotTrade,
    activeEntryOrders,
  } = useContext(EditTradeContext)!;
  const { getExchangePriceFactorLimits } = useContext(
    ExchangesMetadataContext
  )!;

  const priceFactorLimits = getExchangePriceFactorLimits(
    ordersData?.data.exchange
  );
  const ratioPrecision = precisionConfig?.percent || 4;
  const percentPrecision = ratioPrecision / 2;
  const amountPrecision = amountData.precision;
  const [price, setPrice] = useState<string>(
    order ? order.edit_target_price.toString() : currentPrice.toString()
  );
  const [percent, setPercent] = useState<string>(
    order ? toFixed(order.edit_ratio * 100, percentPrecision).toString() : "20"
  );
  const [amount, setAmount] = useState<string>(
    order ? toFixed(order.edit_amount, amountPrecision).toString() : ""
  );

  const priceLimits = getMinAndMaxPrices(
    currentPrice,
    precisionConfig?.price || 0,
    priceFactorLimits
  );

  const numberPrice = parseFloat(price || "0");
  const numberPercent = parseFloat(percent || "0");
  const numberAmount = parseFloat(amount || "0");
  const isShort = !!ordersData?.data.reverse;
  const isBreakout = !!ordersData?.data.breakout;

  const relativePriceErrors = validateOrderRelativePrice(
    numberPrice,
    orderType,
    isShort,
    isBreakout,
    currentPrice,
    entryOrders.filter((order) => order.status === OrderStatus.PENDING),
    takeProfitOrders.filter((order) => order.status === OrderStatus.PENDING)
  );
  const priceRangeError =
    numberPrice < priceLimits.min || numberPrice > priceLimits.max;

  const onOkClick = () => {
    let updatedOrder = {
      ...(order || getDefaultEditOrder()),
      edit_target_price: parseFloat(price),
    };
    if (orderType === OrderTypes.ENTRY) {
      updatedOrder.edit_amount = parseFloat(amount);
      if (order?.id) {
        updatedOrder.edit_quantity =
          (updatedOrder.edit_amount / updatedOrder.amount) *
          updatedOrder.quantity;
      } else {
        updatedOrder.edit_quantity = convertAmountToQuantity(
          updatedOrder.edit_amount,
          updatedOrder.edit_target_price
        );
      }
    } else if (orderType === OrderTypes.TAKE_PROFIT) {
      updatedOrder.edit_ratio = toFixed(
        parseFloat(percent) / 100,
        ratioPrecision
      );
    } else {
      updatedOrder.edit_ratio = 1;
    }
    onOk(updatedOrder);
    closeModal();
  };

  const isUpdatedPriceActiveEntry = () => {
    if (!isBotTrade) return false;
    let prices =
      activeEntryOrders?.map((order) => order.edit_target_price) || [];
    prices.push(numberPrice);
    const sortOrder = getOrdersDefaultSortOrder(
      OrderTypes.ENTRY,
      isShort,
      isBreakout
    );
    prices = prices
      .sort((a, b) => sortByNumberProperty(a, b, undefined, sortOrder))
      .slice(0, ordersData?.data.number_of_active_entries || 0);
    return prices.includes(numberPrice);
  };

  const priceInputData = {
    mainValidationMessage: getValidationMessage(
      t("orders.price"),
      priceLimits.min,
      priceLimits.max
    ),
    mainValidationMessageIsError: priceRangeError,
    precision: precisionConfig?.price || 0,
    name: t("orders.price"),
    value: price,
    onChange: setPrice,
    additionalErrors: relativePriceErrors,
    focusOnMount: true,
  };

  let secondInputData: EditTradeInputProps | undefined;
  let percentRangeError: boolean | undefined;
  let amountRangeError: boolean | undefined;

  if (orderType === OrderTypes.ENTRY) {
    let minAmount = getMinAmountByPrice(numberPrice);
    if (order?.filled_amount) {
      minAmount += order?.filled_amount;
    }
    let orderAvailableAmount: number | undefined;
    if (!isBotTrade || isUpdatedPriceActiveEntry()) {
      orderAvailableAmount = toFixed(
        amountData.getAvailable() + (order?.edit_amount || 0),
        amountPrecision
      );
    }
    amountRangeError =
      (minAmount && numberAmount < minAmount) ||
      (isNumber(orderAvailableAmount) && numberAmount > orderAvailableAmount);
    secondInputData = {
      mainValidationMessage: getAmountValidationMessage(
        minAmount,
        orderAvailableAmount
      ),
      mainValidationMessageIsError: amountRangeError,
      precision: amountPrecision,
      name: t("orders.amount"),
      value: amount,
      onChange: setAmount,
    };
  } else if (orderType === OrderTypes.TAKE_PROFIT) {
    percentRangeError = numberPercent < 1 || numberPercent > 100;
    secondInputData = {
      mainValidationMessage: getValidationMessage(t("orders.ratio"), 1, 100),
      mainValidationMessageIsError: percentRangeError,
      precision: precisionConfig?.percent || 0,
      name: t("orders.ratio"),
      value: percent,
      onChange: setPercent,
      inputPrefix: "%",
    };
  }

  const isError =
    priceRangeError ||
    relativePriceErrors.length > 0 ||
    percentRangeError ||
    amountRangeError;

  return {
    onOkClick,
    priceInputData,
    secondInputData,
    isError,
  };
};
export default useAddOrEditOrderModal;
